import { useCallback, useEffect, useRef, useState } from 'react';
import { Err, ago } from '../ui.js';
import {
  startStrategy, getStrategyJob, listStrategies, applyStrategy, dismissStrategy, ApiError,
} from '../api.js';
import type { AccountStrategy, AgentJob } from '../api.js';

// THE ANGLE — the agent's proposal, and the decision it is waiting on.
//
// This lives inside "The argument" fold because it is the same subject: the
// fold displays wedge, gap and hooks, and this is the thing that fills them.
// Giving it its own fold would have made a second home for one idea, and the
// operator would have to learn which of the two to open.
//
// WHY THERE IS AN APPLY BUTTON AT ALL, instead of the agent just writing.
// A wedge is a judgement and it becomes the basis of every touch on the
// account. Kyle asked to edit before it turns into outreach; the codebase rule
// is that agents propose. Both point at the same button. See strategy-core.ts.
//
// WHY THE SKIPPED LIST IS SHOWN LOUDLY. Apply fills blanks. On an account
// already researched by hand that can mean it writes nothing at all, and an
// apply that silently does nothing reads as a broken button.

const LIVE = ['queued', 'running'];

export function Strategy({ slug, hasWedge, onApplied }: {
  slug: string;
  hasWedge: boolean;
  onApplied: () => void;
}) {
  const [rows, setRows] = useState<AccountStrategy[] | null>(null);
  const [job, setJob] = useState<AgentJob | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const load = useCallback(() => {
    listStrategies(slug).then((r) => setRows(r.strategies)).catch(() => {});
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  // Poll only while a run is live. There is nothing to learn after `done`, and
  // polling a finished job forever quietly burns a battery.
  useEffect(() => {
    if (!job || !LIVE.includes(job.status)) {
      if (timer.current) { window.clearInterval(timer.current); timer.current = null; }
      return;
    }
    timer.current = window.setInterval(() => {
      getStrategyJob(job.id)
        .then((r) => {
          setJob(r.job);
          if (r.job.status === 'done') load();
        })
        .catch(() => {});
    }, 4000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [job?.id, job?.status, load]);

  async function run() {
    setBusy('run'); setError(null); setNote(null);
    try {
      const r = await startStrategy(slug);
      setJob({
        id: r.job_id, status: 'queued', progress: 'Starting…', found: 0, error: null,
        searches: 0, in_tokens: 0, out_tokens: 0,
        created_at: new Date().toISOString(), finished_at: null, output: {},
      });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(null); }
  }

  async function apply(id: string, overwrite: boolean) {
    setBusy(id); setError(null); setNote(null);
    try {
      const r = await applyStrategy(id, overwrite);
      setNote(r.written.length
        ? `Wrote ${r.written.join(', ')} onto the account.`
        : r.note);
      load();
      if (r.written.length) onApplied();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally { setBusy(null); }
  }

  async function drop(id: string) {
    setBusy(id); setError(null); setNote(null);
    try { await dismissStrategy(id); load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : String(e)); }
    finally { setBusy(null); }
  }

  const live = job && LIVE.includes(job.status);
  const open = (rows ?? []).filter((r) => r.status === 'new');
  const past = (rows ?? []).filter((r) => r.status !== 'new');

  return (
    <section className="strategy">
      <Err>{error}</Err>
      {note && <p className="notice">{note}</p>}

      {live && (
        <p className="meta running">
          {job.progress ?? 'Working…'}
          {job.searches > 0 && ` · ${job.searches} searches`}
        </p>
      )}

      {job && job.status === 'failed' && (
        <Err>{job.error ?? 'The run failed.'}</Err>
      )}

      {!live && (
        <div className="toolbar">
          <button
            className={hasWedge ? '' : 'primary'}
            disabled={busy === 'run'}
            onClick={() => void run()}
          >
            {busy === 'run' ? 'Starting…' : open.length ? 'Run it again' : 'Work out the angle'}
          </button>
          {/* Spend is visible, before it is spent. */}
          <span className="cost">web search + tokens — a couple of minutes</span>
        </div>
      )}

      {open.map((s) => (
        <article key={s.id} className="proposal">
          <header>
            <h4>Proposed angle</h4>
            <span className="meta">
              {ago(s.created_at)} · {s.searches} searches · {(s.in_tokens + s.out_tokens).toLocaleString()} tokens
            </span>
          </header>

          {s.wedge && <section><h5>Wedge</h5><p>{s.wedge}</p></section>}
          {s.entry_persona && <section><h5>Enter through</h5><p>{s.entry_persona}</p></section>}
          {s.gap_statement && <section><h5>The gap</h5><p>{s.gap_statement}</p></section>}
          {s.squeeze_note && <section><h5>Competitive squeeze</h5><p>{s.squeeze_note}</p></section>}

          {s.hooks.length > 0 && (
            <section>
              <h5>Hooks</h5>
              <ul>{s.hooks.map((h, i) => <li key={i}>{h}</li>)}</ul>
            </section>
          )}
          {s.proof_points.length > 0 && (
            <section>
              <h5>Proof points</h5>
              <ul>{s.proof_points.map((p, i) => <li key={i}>{p}</li>)}</ul>
            </section>
          )}
          {s.rationale && <section><h5>Why this angle</h5><p>{s.rationale}</p></section>}
          {s.objections && <section><h5>What they will push back with</h5><p>{s.objections}</p></section>}

          {/* An angle with no source is an opinion. Sources are part of the
              proposal, not a footnote to it. */}
          {s.evidence.length > 0 && (
            <section>
              <h5>What it read</h5>
              <ul className="evidence">
                {s.evidence.map((e, i) => (
                  <li key={i}>
                    {e.claim}
                    {e.source_url && (
                      <> — <a href={e.source_url} target="_blank" rel="noreferrer">source</a></>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {s.unknowns && (
            <section><h5>What it could not establish</h5><p className="meta">{s.unknowns}</p></section>
          )}

          <div className="toolbar">
            <button
              className="primary"
              disabled={busy === s.id}
              onClick={() => void apply(s.id, false)}
            >
              {busy === s.id ? 'Applying…' : 'Apply to the account'}
            </button>
            <button disabled={busy === s.id} onClick={() => void apply(s.id, true)}>
              Apply, replacing what is there
            </button>
            <button disabled={busy === s.id} onClick={() => void drop(s.id)}>
              Dismiss
            </button>
          </div>
        </article>
      ))}

      {past.length > 0 && (
        <p className="meta">
          {past.length} earlier {past.length === 1 ? 'proposal' : 'proposals'} on this account
          {' '}({past.filter((p) => p.status === 'applied').length} applied).
        </p>
      )}
    </section>
  );
}
